/** Helpers and small views shared by the time-tracking pages. */
import { useTranslation } from "react-i18next";
import { Message } from "@primereact/ui/message";
import { ApiError } from "../../lib/api.js";
import type { TimesheetData } from "./types.js";
export const KINDS = [
  "vacation",
  "sick",
  "military",
  "accident",
  "compensation_leave",
  "unpaid_leave",
  "other_absence",
] as const;
export type TimeEntry = TimesheetData["entries"][number];
export function dateOnly(date: Date): string {
  const year = date.getFullYear();
  return `${year}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
export function monday(value = new Date()): string {
  const date = new Date(value.getFullYear(), value.getMonth(), value.getDate());
  const weekday = date.getDay() || 7;
  date.setDate(date.getDate() - weekday + 1);
  return dateOnly(date);
}
export function shiftWeek(week: string, days: number): string {
  const date = new Date(`${week}T12:00:00`);
  date.setDate(date.getDate() + days);
  return dateOnly(date);
}
export function duration(minutes: number): string {
  const sign = minutes < 0 ? "−" : "";
  const absolute = Math.abs(minutes);
  return `${sign}${Math.floor(absolute / 60)}:${String(absolute % 60).padStart(2, "0")}`;
}
export function localTime(iso: string | null): string {
  return iso
    ? new Intl.DateTimeFormat(undefined, {
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(iso))
    : "—";
}
export function timeInputValue(iso: string | null): string {
  if (!iso) return "";
  const parts = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(iso));
  const hour = parts.find((part) => part.type === "hour")?.value ?? "00";
  const minute = parts.find((part) => part.type === "minute")?.value ?? "00";
  return `${hour}:${minute}`;
}
export function calendarValue(value: string): Date | null {
  return value ? new Date(`${value}T12:00:00`) : null;
}
export function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-app-surface p-4 shadow-sm ring-1 ring-app-border">
      <p className="text-xs font-medium uppercase tracking-wide text-app-muted">
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold text-app-text">{value}</p>
    </div>
  );
}
export function DayValue({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-app-muted">{label}</dt>
      <dd
        className={
          emphasis ? "font-semibold text-brand" : "font-medium text-app-text"
        }
      >
        {value}
      </dd>
    </div>
  );
}
export function TimeMutationMessage({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  return (
    <Message.Root className="sm:col-span-2" severity="error">
      <Message.Content>
        <Message.Text>
          {/* API messages arrive translated; anything else is generic. */}
          {error instanceof ApiError
            ? error.message
            : t("time.workflow.errors.generic")}
        </Message.Text>
      </Message.Content>
    </Message.Root>
  );
}
